import { defineStore } from "pinia";
import { i18n } from "@/i18n";
import { broadcastSupportAnnouncement, claimSupportTicket, deleteMessage as deleteMessageApi, ensureMyCourseConversations, ensureMySupportConversations, getConversationMessages, getConversations, markConversationRead, resolveSupportTicket, setConversationHidden, setConversationMute, } from "@/services/messagingApiService";
import { messagingSocketService, } from "@/services/messagingSocketService";
import { useYearStore } from "@/store/yearStore";
import { uploadChatAttachment } from "@/utils/uploadChatAttachment";
import { supportTicketUnreadForAgent } from "@/utils/supportTicketUnread";
import { maxSortKey, messageSortKeyOf, } from "@/utils/messageReceipt";
export const mentionDisplayText = (displayName) => {
    const slug = String(displayName || "")
        .trim()
        .replace(/\s+/g, "_")
        .replace(/[^\wÁÉÍÓÚÜÑáéíóúüñ._-]/gi, "");
    return `@${slug || "usuario"}`;
};
export const recomputeMentions = (body, mentions) => {
    const next = [];
    let cursor = 0;
    for (const mention of mentions) {
        const text = String(mention.displayText || "");
        if (!text)
            continue;
        const idx = body.indexOf(text, cursor);
        if (idx < 0)
            continue;
        next.push({
            userId: String(mention.userId || "")
                .trim()
                .toLowerCase(),
            displayText: text,
            startIndex: idx,
            length: text.length,
        });
        cursor = idx + text.length;
    }
    return next;
};
export const REACTION_EMOJIS = [
    "👍",
    "❤️",
    "😂",
    "🎉",
    "👏",
    "🙏",
    "✅",
    "❓",
    "😮",
    "😢",
];
export const currentMessagingUserId = () => String(localStorage.getItem("userEmail") ||
    localStorage.getItem("email") ||
    "")
    .trim()
    .toLowerCase();
const normalizeMessageStatus = (raw) => {
    const value = String(raw || "").toLowerCase();
    if (value === "pending" || value === "failed" || value === "uploading") {
        return value;
    }
    return "sent";
};
const asMessage = (payload, conversationIdFallback) => {
    const senderId = String(payload.senderUserId ?? payload.senderId ?? "").trim();
    const rawName = String(payload.senderDisplayName ?? payload.senderName ?? "").trim();
    const attachment = payload.attachment;
    const type = String(payload.type || payload.messageType || "TEXT").toUpperCase();
    const replyRaw = payload.replyTo;
    const replyTo = replyRaw
        ? {
            messageId: String(replyRaw.messageId || ""),
            senderUserId: String(replyRaw.senderUserId || ""),
            senderDisplayName: String(replyRaw.senderDisplayName || ""),
            bodyPreview: String(replyRaw.bodyPreview || ""),
            type: String(replyRaw.type || "TEXT").toUpperCase(),
        }
        : null;
    const reactionsRaw = payload.reactions;
    const reactions = {};
    if (reactionsRaw && typeof reactionsRaw === "object") {
        for (const [emoji, users] of Object.entries(reactionsRaw)) {
            if (!Array.isArray(users) || !users.length)
                continue;
            reactions[emoji] = users.map((user) => {
                if (typeof user === "string") {
                    return { userId: user.toLowerCase(), displayName: user };
                }
                const row = user;
                const uid = String(row.userId || row.email || "")
                    .trim()
                    .toLowerCase();
                return {
                    userId: uid,
                    displayName: String(row.displayName || row.name || uid).trim() || uid,
                };
            });
        }
    }
    const message = {
        id: String(payload.messageId ?? payload.id ?? payload.clientMessageId ?? ""),
        clientId: (payload.clientMessageId ?? payload.clientId),
        conversationId: String(payload.conversationId ?? conversationIdFallback ?? ""),
        body: String(payload.body ?? payload.text ?? ""),
        createdAt: String(payload.createdAt ?? new Date().toISOString()),
        senderName: rawName || senderId || undefined,
        senderId: senderId || undefined,
        senderStudentName: payload.senderStudentName ?? null,
        type: type === "IMAGE" || type === "FILE" ? type : "TEXT",
        card: (() => {
            const raw = payload.card;
            if (!raw || typeof raw !== "object")
                return null;
            const kind = String(raw.kind || "").toUpperCase();
            if (kind !== "FUND_CREATED")
                return null;
            const title = String(raw.title || "").trim();
            if (!title)
                return null;
            return {
                kind: "FUND_CREATED",
                title,
                amountLabel: raw.amountLabel
                    ? String(raw.amountLabel)
                    : raw.amount_label
                        ? String(raw.amount_label)
                        : undefined,
                periodLabel: raw.periodLabel
                    ? String(raw.periodLabel)
                    : raw.period_label
                        ? String(raw.period_label)
                        : undefined,
                centroCostoId: raw.centroCostoId
                    ? String(raw.centroCostoId)
                    : raw.centro_costo_id
                        ? String(raw.centro_costo_id)
                        : undefined,
                schoolId: raw.schoolId ? String(raw.schoolId) : undefined,
                year: raw.year ? String(raw.year) : undefined,
                courseCode: raw.courseCode
                    ? String(raw.courseCode)
                    : raw.course_code
                        ? String(raw.course_code)
                        : undefined,
            };
        })(),
        attachment: attachment || null,
        attachmentUrl: payload.attachmentUrl ?? null,
        replyTo,
        reactions: Object.keys(reactions).length ? reactions : undefined,
        mentions: Array.isArray(payload.mentions)
            ? payload.mentions
                .map((item) => ({
                userId: String(item.userId || "")
                    .trim()
                    .toLowerCase(),
                displayText: String(item.displayText || ""),
                startIndex: Number(item.startIndex) || 0,
                length: Number(item.length) || 0,
            }))
                .filter((item) => item.userId && item.displayText && item.length > 0)
            : undefined,
        editedAt: payload.editedAt ?? null,
        deletedAt: payload.deletedAt ?? null,
        status: normalizeMessageStatus(payload.status),
        sortKey: String(payload.sortKey ||
            payload.SK ||
            (payload.createdAt && (payload.messageId || payload.id)
                ? `MESSAGE#${payload.createdAt}#${payload.messageId || payload.id}`
                : "")) || undefined,
    };
    if (message.deletedAt) {
        message.body = "";
        message.type = "TEXT";
        message.card = null;
        message.attachment = null;
        message.attachmentUrl = null;
        message.mentions = undefined;
        message.reactions = undefined;
    }
    return message;
};
const applyLocalReaction = (reactions, userId, emoji, displayName) => {
    const me = userId.trim().toLowerCase();
    const next = {};
    for (const [key, users] of Object.entries(reactions || {})) {
        const filtered = users.filter((user) => user.userId !== me);
        if (filtered.length)
            next[key] = filtered;
    }
    const already = (reactions?.[emoji] || []).some((user) => user.userId === me);
    if (already)
        return next;
    next[emoji] = [
        ...(next[emoji] || []),
        { userId: me, displayName: displayName || me },
    ];
    return next;
};
export const isOwnMessage = (message) => {
    const me = currentMessagingUserId();
    if (message.senderId && me) {
        return message.senderId.toLowerCase() === me;
    }
    return (Boolean(message.clientId) &&
        (message.status === "pending" ||
            message.status === "failed" ||
            message.status === "uploading"));
};
const isRetryableMessagingError = (error) => {
    if (!error || typeof error !== "object")
        return true;
    const status = error.response
        ?.status;
    if (!status)
        return true;
    return status >= 500 || status === 429;
};
let conversationRetryTimer = null;
let conversationRetryAttempt = 0;
let onlineHandler = null;
let visibleHandler = null;
const clearConversationRetry = () => {
    if (conversationRetryTimer) {
        clearTimeout(conversationRetryTimer);
        conversationRetryTimer = null;
    }
};
const EMPTY_RECEIPTS = {
    lastReadSortKey: null,
    lastDeliveredSortKey: null,
};
const unreadOf = (conversation) => {
    const count = Number(conversation.unreadCount || 0);
    if (count > 0)
        return count;
    return conversation.unread ? 1 : 0;
};
export const useMessagingStore = defineStore("messaging", {
    state: () => ({
        connectionStatus: "disconnected",
        conversations: [],
        messagesByConversationId: {},
        pendingByClientId: {},
        activeConversationId: null,
        pushNavIntent: null,
        seenMessageIds: new Set(),
        initialized: false,
        loading: false,
        conversationsLoadedAt: 0,
        conversationsYear: "",
        error: "",
        uploading: false,
        isSupportAgent: false,
        typingByConversationId: {},
        receiptsByConversationId: {},
        appForeground: true,
    }),
    getters: {
        totalUnreadCount: (state) => state.conversations.reduce((sum, conversation) => sum + unreadOf(conversation), 0),
        unreadBadgeLabel: (state) => {
            const total = state.conversations.reduce((sum, conversation) => sum + unreadOf(conversation), 0);
            if (total <= 0)
                return "";
            if (total > 99)
                return "99+";
            return String(total);
        },
        hasUnreadConversations: (state) => state.conversations.some((conversation) => unreadOf(conversation) > 0),
    },
    actions: {
        async initMessaging() {
            if (this.initialized)
                return;
            this.initialized = true;
            messagingSocketService.setHandlers((event) => this.handleSocketEvent(event), (status) => {
                this.connectionStatus = status;
            });
            onlineHandler = () => this.onBrowserOnline();
            visibleHandler = () => this.onBrowserVisible();
            window.addEventListener("online", onlineHandler);
            document.addEventListener("visibilitychange", visibleHandler);
            try {
                await this.loadConversations();
            }
            catch {
                this.scheduleConversationRetry();
            }
            await messagingSocketService.connect();
        },
        async shutdownMessaging() {
            clearConversationRetry();
            conversationRetryAttempt = 0;
            messagingSocketService.disconnect();
            if (onlineHandler) {
                window.removeEventListener("online", onlineHandler);
                onlineHandler = null;
            }
            if (visibleHandler) {
                document.removeEventListener("visibilitychange", visibleHandler);
                visibleHandler = null;
            }
            this.connectionStatus = "disconnected";
            this.activeConversationId = null;
            this.conversations = [];
            this.messagesByConversationId = {};
            this.pendingByClientId = {};
            this.seenMessageIds = new Set();
            this.pushNavIntent = null;
            this.conversationsLoadedAt = 0;
            this.conversationsYear = "";
            this.initialized = false;
            this.error = "";
        },
        messagingYear() {
            const yearStore = useYearStore();
            return String(yearStore.añoActivo || new Date().getFullYear());
        },
        async loadConversations(options = {}) {
            const year = this.messagingYear();
            const hasFreshCache = this.conversationsYear === year &&
                this.conversations.length > 0 &&
                Date.now() - this.conversationsLoadedAt < 30000;
            if (!options.force && hasFreshCache)
                return;
            this.loading = true;
            this.error = "";
            try {
                if (options.ensure !== false) {
                    await ensureMyCourseConversations(year);
                    const support = await ensureMySupportConversations();
                    this.isSupportAgent = Boolean(support?.isAgent);
                }
                const items = await getConversations(year);
                const me = currentMessagingUserId();
                this.conversations = [...items]
                    .filter((conversation) => {
                    if (!this.isSupportAgent)
                        return true;
                    const type = String(conversation.type || "").toUpperCase();
                    if (type !== "SUPPORT")
                        return true;
                    return String(conversation.supportStatus || "").toUpperCase() !== "RESOLVED";
                })
                    .map((conversation) => {
                    const lastSender = String(conversation.lastMessageSenderUserId || "").toLowerCase();
                    let unread = Boolean(conversation.unread);
                    let unreadCount = conversation.unreadCount || 0;
                    if (me && lastSender && lastSender === me) {
                        unread = false;
                        unreadCount = 0;
                    }
                    const type = String(conversation.type || "").toUpperCase();
                    if (this.isSupportAgent && type === "SUPPORT") {
                        unread = supportTicketUnreadForAgent({
                            supportStatus: conversation.supportStatus,
                            claimedBy: conversation.claimedBy,
                            requesterUserId: conversation.requesterUserId,
                            lastMessageSenderUserId: lastSender,
                            viewerUserId: me,
                            baseUnread: unread,
                        });
                        unreadCount = unread ? conversation.unreadCount || 1 : 0;
                    }
                    return { ...conversation, unread, unreadCount };
                })
                    .sort((a, b) => {
                    const atA = a.lastMessageAt ? new Date(a.lastMessageAt).getTime() : 0;
                    const atB = b.lastMessageAt ? new Date(b.lastMessageAt).getTime() : 0;
                    if (atA !== atB)
                        return atB - atA;
                    const typeRank = (item) => {
                        const type = String(item.type || "").toUpperCase();
                        if (type === "SUPPORT" || type === "ANNOUNCEMENT")
                            return 1;
                        return 0;
                    };
                    return typeRank(a) - typeRank(b);
                });
                this.conversationsYear = year;
                this.conversationsLoadedAt = Date.now();
                this.error = "";
                conversationRetryAttempt = 0;
                clearConversationRetry();
            }
            catch (error) {
                const axiosMessage = error &&
                    typeof error === "object" &&
                    "response" in error &&
                    error.response?.data
                        ?.error;
                this.error =
                    axiosMessage ||
                        (error instanceof Error ? error.message : "No se pudieron cargar chats");
                if (!this.conversations.length || this.conversationsYear !== year) {
                    this.conversations = [];
                }
                if (isRetryableMessagingError(error)) {
                    this.scheduleConversationRetry();
                }
                throw error;
            }
            finally {
                this.loading = false;
            }
        },
        onBrowserOnline() {
            messagingSocketService.reconnectIfNeeded();
            void this.recoverMessaging();
        },
        onBrowserVisible() {
            if (document.visibilityState !== "visible")
                return;
            messagingSocketService.reconnectIfNeeded();
            void this.recoverMessaging();
        },
        scheduleConversationRetry() {
            if (!this.initialized || conversationRetryTimer)
                return;
            const delay = Math.min(15000, 1000 * 2 ** conversationRetryAttempt);
            conversationRetryTimer = setTimeout(() => {
                conversationRetryTimer = null;
                conversationRetryAttempt += 1;
                void this.recoverMessaging();
            }, delay);
        },
        async recoverMessaging() {
            if (!this.initialized)
                return;
            messagingSocketService.reconnectIfNeeded();
            if (this.loading)
                return;
            try {
                await this.loadConversations({
                    force: Boolean(this.error),
                    ensure: this.conversations.length === 0,
                });
            }
            catch {
                if (this.error)
                    this.scheduleConversationRetry();
            }
        },
        upsertConversation(conversation) {
            const idx = this.conversations.findIndex((item) => item.conversationId === conversation.conversationId ||
                item.id === conversation.conversationId);
            if (idx >= 0) {
                this.conversations[idx] = {
                    ...this.conversations[idx],
                    ...conversation,
                };
                return;
            }
            this.conversations = [conversation, ...this.conversations];
        },
        async openConversation(conversationId, before) {
            this.activeConversationId = conversationId;
            messagingSocketService.send("chat.active", {
                conversationId,
                appState: "FOREGROUND",
            });
            const result = await getConversationMessages(conversationId, {
                limit: 50,
                before,
            });
            const incoming = (result.items ?? []).map((item) => asMessage(item, conversationId));
            const existing = this.messagesByConversationId[conversationId] ?? [];
            const merged = [...incoming, ...existing].filter((message, index, list) => list.findIndex((item) => item.id === message.id) === index);
            this.messagesByConversationId[conversationId] = merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            if (result.receipts)
                this.applyReceipts(conversationId, result.receipts);
            return incoming.length;
        },
        async fetchAllMessages(conversationId) {
            const pageSize = 50;
            const maxPages = 30;
            let before;
            let previousOldest = "";
            const collected = [];
            const seen = new Set();
            for (let page = 0; page < maxPages; page += 1) {
                const result = await getConversationMessages(conversationId, {
                    limit: pageSize,
                    before,
                });
                const incoming = (result.items ?? []).map((item) => asMessage(item, conversationId));
                if (!incoming.length)
                    break;
                for (const message of incoming) {
                    const key = String(message.id || message.clientId || "");
                    if (!key || seen.has(key))
                        continue;
                    seen.add(key);
                    collected.push(message);
                }
                const oldest = incoming.reduce((current, message) => message.createdAt < current ? message.createdAt : current, incoming[0].createdAt);
                if (incoming.length < pageSize)
                    break;
                if (!oldest || oldest === previousOldest)
                    break;
                previousOldest = oldest;
                before = oldest;
            }
            const existing = this.messagesByConversationId[conversationId] ?? [];
            const merged = [...collected, ...existing].filter((message, index, list) => list.findIndex((item) => String(item.id || item.clientId || "") ===
                String(message.id || message.clientId || "")) === index);
            merged.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
            this.messagesByConversationId[conversationId] = merged;
            return merged;
        },
        async sendMessage(conversationId, body, options = {}) {
            const clientMessageId = crypto.randomUUID();
            const me = currentMessagingUserId();
            const replyTo = options.replyTo || null;
            const mentions = recomputeMentions(body, options.mentions || []);
            const pending = asMessage({
                clientMessageId,
                conversationId,
                body,
                type: "TEXT",
                status: "pending",
                createdAt: new Date().toISOString(),
                senderUserId: me,
                senderDisplayName: me,
                replyTo,
                mentions,
            });
            this.pendingByClientId[clientMessageId] = pending;
            this.messagesByConversationId[conversationId] = [
                ...(this.messagesByConversationId[conversationId] ?? []),
                pending,
            ];
            const payload = {
                conversationId,
                body,
                clientMessageId,
                type: "TEXT",
            };
            if (replyTo?.messageId)
                payload.replyToMessageId = replyTo.messageId;
            if (mentions.length)
                payload.mentions = mentions;
            const sent = messagingSocketService.send("chat.send", payload);
            if (!sent) {
                pending.status = "failed";
            }
            else {
                this.clearConversationUnread(conversationId);
            }
        },
        async muteConversation(conversationId, duration) {
            const muted = duration !== "off";
            const data = await setConversationMute(conversationId, {
                muted,
                duration,
            });
            const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
            if (idx >= 0) {
                this.conversations[idx] = {
                    ...this.conversations[idx],
                    muted: data.isMuted ?? data.muted,
                    mutedUntil: data.mutedUntil ?? null,
                };
            }
            messagingSocketService.send("chat.mute", {
                conversationId,
                muted,
                duration,
            });
            return data;
        },
        async hideConversation(conversationId) {
            const data = await setConversationHidden(conversationId, true);
            this.conversations = this.conversations.filter((item) => item.conversationId !== conversationId && item.id !== conversationId);
            messagingSocketService.send("chat.hide", {
                conversationId,
                hidden: true,
            });
            return data;
        },
        async claimSupportConversation(conversationId) {
            const data = await claimSupportTicket(conversationId);
            const me = currentMessagingUserId();
            const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
            if (idx >= 0) {
                this.conversations[idx] = {
                    ...this.conversations[idx],
                    supportStatus: "CLAIMED",
                    claimedBy: me,
                    claimedAt: new Date().toISOString(),
                };
            }
            await this.loadConversations({ force: true, ensure: false });
            return data;
        },
        async resolveSupportConversation(conversationId) {
            const data = await resolveSupportTicket(conversationId);
            this.conversations = this.conversations.filter((item) => item.conversationId !== conversationId && item.id !== conversationId);
            await this.loadConversations({ force: true, ensure: false });
            return data;
        },
        async broadcastAnnouncement(body) {
            const data = await broadcastSupportAnnouncement(body);
            await this.loadConversations({ force: true });
            return data;
        },
        async sendAttachment(conversationId, file, caption = "", options = {}) {
            const authToken = localStorage.getItem("authToken") || "";
            if (!authToken)
                throw new Error("Sesión no válida");
            const clientMessageId = crypto.randomUUID();
            const me = currentMessagingUserId();
            const isImage = String(file.type || "").startsWith("image/");
            const isAudio = String(file.type || "").startsWith("audio/");
            const localUrl = URL.createObjectURL(file);
            const replyTo = options.replyTo || null;
            const defaultCaption = isImage
                ? "📷 Imagen"
                : isAudio
                    ? "🎤 Nota de voz"
                    : file.name;
            const pending = asMessage({
                clientMessageId,
                conversationId,
                body: caption || defaultCaption,
                type: isImage ? "IMAGE" : "FILE",
                status: "uploading",
                createdAt: new Date().toISOString(),
                senderUserId: me,
                senderDisplayName: me,
                replyTo,
                attachment: {
                    key: "",
                    fileName: file.name,
                    contentType: file.type || "application/octet-stream",
                    size: file.size,
                },
                attachmentUrl: localUrl,
            });
            this.pendingByClientId[clientMessageId] = pending;
            this.messagesByConversationId[conversationId] = [
                ...(this.messagesByConversationId[conversationId] ?? []),
                pending,
            ];
            this.uploading = true;
            try {
                const uploaded = await uploadChatAttachment({
                    conversationId,
                    file,
                    authToken,
                });
                pending.status = "pending";
                pending.attachment = uploaded;
                const payload = {
                    conversationId,
                    body: caption,
                    clientMessageId,
                    type: isImage ? "IMAGE" : "FILE",
                    attachment: uploaded,
                };
                if (replyTo?.messageId)
                    payload.replyToMessageId = replyTo.messageId;
                const sent = messagingSocketService.send("chat.send", payload);
                if (!sent)
                    pending.status = "failed";
                else
                    this.clearConversationUnread(conversationId);
            }
            catch (error) {
                pending.status = "failed";
                throw error;
            }
            finally {
                this.uploading = false;
            }
        },
        async retryMessage(conversationId, clientId) {
            const pending = this.pendingByClientId[clientId];
            if (!pending)
                return;
            pending.status = "pending";
            const payload = {
                conversationId,
                body: pending.body,
                clientMessageId: clientId,
                type: pending.type || "TEXT",
            };
            if (pending.attachment?.key)
                payload.attachment = pending.attachment;
            if (pending.replyTo?.messageId) {
                payload.replyToMessageId = pending.replyTo.messageId;
            }
            const sent = messagingSocketService.send("chat.send", payload);
            if (!sent)
                pending.status = "failed";
            else
                this.clearConversationUnread(conversationId);
        },
        clearConversationUnread(conversationId) {
            const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
            if (idx < 0)
                return;
            this.conversations[idx] = {
                ...this.conversations[idx],
                unread: false,
                unreadCount: 0,
            };
        },
        async markRead(conversationId, messageId, sortKey) {
            const messages = this.messagesByConversationId[conversationId] ?? [];
            const target = messages.find((item) => item.id === messageId);
            const cursor = sortKey ||
                (target ? messageSortKeyOf(target) : "") ||
                "";
            messagingSocketService.send("chat.read", {
                conversationId,
                messageId,
                ...(cursor ? { sortKey: cursor } : {}),
            });
            this.clearConversationUnread(conversationId);
            try {
                await markConversationRead(conversationId, messageId, cursor || undefined);
            }
            catch {
                // el socket ya intentó; un GET posterior relee los cursores
            }
        },
        reactToMessage(conversationId, messageId, emoji) {
            if (!conversationId || !messageId || !emoji)
                return;
            const me = currentMessagingUserId();
            if (!me)
                return;
            const messages = this.messagesByConversationId[conversationId] ?? [];
            const current = messages.find((item) => item.id === messageId);
            if (!current)
                return;
            const optimistic = applyLocalReaction(current.reactions, me, emoji, "Tú");
            this.messagesByConversationId[conversationId] = messages.map((item) => item.id === messageId
                ? {
                    ...item,
                    reactions: Object.keys(optimistic).length
                        ? optimistic
                        : undefined,
                }
                : item);
            const sent = messagingSocketService.send("chat.react", {
                conversationId,
                messageId,
                emoji,
            });
            if (!sent) {
                // revertir si no hay socket
                this.messagesByConversationId[conversationId] = messages;
            }
        },
        editMessage(conversationId, messageId, body, mentions) {
            if (!conversationId || !messageId || !body.trim())
                return;
            const messages = this.messagesByConversationId[conversationId] ?? [];
            const current = messages.find((item) => item.id === messageId);
            if (!current)
                return;
            const optimisticEditedAt = new Date().toISOString();
            this.messagesByConversationId[conversationId] = messages.map((item) => item.id === messageId
                ? {
                    ...item,
                    body: body.trim(),
                    editedAt: optimisticEditedAt,
                    mentions: mentions?.length ? mentions : undefined,
                }
                : item);
            const payload = {
                conversationId,
                messageId,
                body: body.trim(),
            };
            if (mentions?.length)
                payload.mentions = mentions;
            const sent = messagingSocketService.send("chat.edit", payload);
            if (!sent) {
                this.messagesByConversationId[conversationId] = messages;
            }
        },
        applyMessageDeleted(conversationId, messageId, deletedAt) {
            if (!conversationId || !messageId)
                return;
            const at = deletedAt || new Date().toISOString();
            const deletedPreview = String(i18n.global.t("messaging.chat.deletedMessagePreview"));
            const messages = this.messagesByConversationId[conversationId] ?? [];
            this.messagesByConversationId[conversationId] = messages.map((item) => item.id === messageId
                ? {
                    ...item,
                    deletedAt: at,
                    body: "",
                    type: "TEXT",
                    card: null,
                    attachment: null,
                    attachmentUrl: null,
                    mentions: undefined,
                    reactions: undefined,
                }
                : item);
            const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
            if (idx >= 0) {
                const conv = this.conversations[idx];
                const msg = messages.find((item) => item.id === messageId);
                const lastAt = conv.lastMessageAt
                    ? new Date(conv.lastMessageAt).getTime()
                    : 0;
                if (msg && new Date(msg.createdAt).getTime() >= lastAt) {
                    this.conversations[idx] = {
                        ...conv,
                        lastMessage: deletedPreview,
                    };
                }
            }
        },
        async deleteMessage(conversationId, messageId) {
            if (!conversationId || !messageId)
                return;
            const messages = this.messagesByConversationId[conversationId] ?? [];
            const current = messages.find((item) => item.id === messageId);
            if (!current || current.deletedAt)
                return;
            const previous = [...messages];
            this.applyMessageDeleted(conversationId, messageId);
            if (current.status === "pending" || current.status === "failed") {
                return;
            }
            const sent = messagingSocketService.send("chat.delete", {
                conversationId,
                messageId,
            });
            if (sent)
                return;
            try {
                await deleteMessageApi(conversationId, messageId);
            }
            catch {
                this.messagesByConversationId[conversationId] = previous;
            }
        },
        sendTyping(conversationId, typing) {
            if (!conversationId)
                return;
            messagingSocketService.send("chat.typing", { conversationId, typing });
        },
        setTypingState(conversationId, userId, typing, ttlMs = 5000) {
            if (!conversationId || !userId)
                return;
            const me = currentMessagingUserId();
            if (me && userId.toLowerCase() === me)
                return;
            const current = { ...(this.typingByConversationId[conversationId] || {}) };
            if (typing) {
                current[userId.toLowerCase()] = Date.now() + ttlMs;
            }
            else {
                delete current[userId.toLowerCase()];
            }
            this.typingByConversationId = {
                ...this.typingByConversationId,
                [conversationId]: current,
            };
        },
        pruneTypingStates() {
            const now = Date.now();
            const next = {};
            for (const [conversationId, usersRaw] of Object.entries(this.typingByConversationId)) {
                const users = usersRaw;
                const active = {};
                for (const [userId, until] of Object.entries(users)) {
                    if (until > now)
                        active[userId] = until;
                }
                if (Object.keys(active).length)
                    next[conversationId] = active;
            }
            this.typingByConversationId = next;
        },
        applyMessageUpdate(conversationId, messageId, body, editedAt, mentions) {
            if (!conversationId || !messageId)
                return;
            const messages = this.messagesByConversationId[conversationId] ?? [];
            this.messagesByConversationId[conversationId] = messages.map((item) => item.id === messageId
                ? {
                    ...item,
                    body,
                    editedAt: editedAt || item.editedAt || new Date().toISOString(),
                    mentions: mentions?.length ? mentions : undefined,
                }
                : item);
            const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
            if (idx >= 0) {
                const conv = this.conversations[idx];
                const lastAt = conv.lastMessageAt
                    ? new Date(conv.lastMessageAt).getTime()
                    : 0;
                const msg = messages.find((item) => item.id === messageId);
                if (msg && new Date(msg.createdAt).getTime() >= lastAt) {
                    this.conversations[idx] = {
                        ...conv,
                        lastMessage: body.slice(0, 140),
                    };
                }
            }
        },
        applyMessageReactions(conversationId, messageId, reactions) {
            if (!conversationId || !messageId)
                return;
            const parsed = asMessage({ messageId, reactions: reactions || {} }, conversationId).reactions;
            const messages = this.messagesByConversationId[conversationId] ?? [];
            this.messagesByConversationId[conversationId] = messages.map((item) => item.id === messageId
                ? { ...item, reactions: parsed }
                : item);
        },
        applyReceipts(conversationId, incoming) {
            if (!conversationId)
                return;
            const current = this.receiptsByConversationId[conversationId] || {};
            const next = {
                lastReadSortKey: maxSortKey(current.lastReadSortKey, incoming.lastReadSortKey),
                lastDeliveredSortKey: maxSortKey(current.lastDeliveredSortKey, incoming.lastDeliveredSortKey),
            };
            this.receiptsByConversationId = {
                ...this.receiptsByConversationId,
                [conversationId]: next,
            };
        },
        receiptsOf(conversationId) {
            return this.receiptsByConversationId[conversationId] || EMPTY_RECEIPTS;
        },
        handleSocketEvent(event) {
            if (event.type === "connection.ready") {
                messagingSocketService.send("chat.active", {
                    conversationId: this.activeConversationId,
                    appState: "FOREGROUND",
                });
                void this.loadConversations().catch(() => undefined);
                return;
            }
            if (event.type === "chat.message.ack") {
                const clientId = String(event.clientMessageId ?? event.clientId ?? "");
                const pending = this.pendingByClientId[clientId];
                if (!pending)
                    return;
                const acknowledged = asMessage({
                    ...pending,
                    messageId: event.messageId,
                    createdAt: event.createdAt,
                    status: "sent",
                    clientMessageId: clientId,
                    type: event.messageType || pending.type,
                    attachment: event.attachment || pending.attachment,
                    replyTo: event.replyTo || pending.replyTo,
                    mentions: event.mentions || pending.mentions,
                    senderDisplayName: event.senderDisplayName || pending.senderName,
                    senderStudentName: event.senderStudentName ?? pending.senderStudentName,
                    body: event.body || pending.body,
                    sortKey: event.sortKey || event.SK,
                }, pending.conversationId);
                const messages = this.messagesByConversationId[pending.conversationId] ?? [];
                this.messagesByConversationId[pending.conversationId] = messages.map((message) => message.clientId === clientId ? acknowledged : message);
                delete this.pendingByClientId[clientId];
                if (acknowledged.id)
                    this.seenMessageIds.add(acknowledged.id);
                this.clearConversationUnread(pending.conversationId);
                return;
            }
            if (event.type === "chat.message.created") {
                const nested = event.message || event;
                const conversationId = String(event.conversationId || nested.conversationId || "");
                const message = asMessage(nested, conversationId);
                if (!message.id || this.seenMessageIds.has(message.id))
                    return;
                this.seenMessageIds.add(message.id);
                const previous = this.messagesByConversationId[conversationId] ?? [];
                const withoutPending = [];
                for (const item of previous) {
                    if (item.clientId && item.clientId === message.clientId)
                        continue;
                    withoutPending.push(item);
                }
                withoutPending.push(message);
                withoutPending.sort((left, right) => new Date(left.createdAt).getTime() -
                    new Date(right.createdAt).getTime());
                this.messagesByConversationId[conversationId] = withoutPending;
                if (isOwnMessage(message)) {
                    this.clearConversationUnread(conversationId);
                }
                void this.loadConversations({ force: true, ensure: false }).catch(() => undefined);
                return;
            }
            if (event.type === "chat.message.deleted" || event.type === "chat.delete.ack") {
                const conversationId = String(event.conversationId || "");
                const messageId = String(event.messageId || "");
                const deletedAt = event.deletedAt ?? null;
                if (!conversationId || !messageId)
                    return;
                this.applyMessageDeleted(conversationId, messageId, deletedAt);
                void this.loadConversations({ force: true, ensure: false }).catch(() => undefined);
                return;
            }
            if (event.type === "chat.message.updated" ||
                event.type === "chat.edit.ack") {
                const conversationId = String(event.conversationId || "");
                const messageId = String(event.messageId || "");
                this.applyMessageUpdate(conversationId, messageId, String(event.body || ""), event.editedAt ?? null, event.mentions);
                return;
            }
            if (event.type === "chat.typing") {
                const conversationId = String(event.conversationId || "");
                const userId = String(event.userId || "")
                    .trim()
                    .toLowerCase();
                this.setTypingState(conversationId, userId, event.typing !== false);
                return;
            }
            if (event.type === "chat.message.reaction" ||
                event.type === "chat.reaction.ack") {
                const conversationId = String(event.conversationId || "");
                const messageId = String(event.messageId || "");
                this.applyMessageReactions(conversationId, messageId, event.reactions);
                return;
            }
            if (event.type === "chat.read" || event.type === "chat.delivered") {
                const conversationId = String(event.conversationId || "");
                const actor = String(event.userId || "")
                    .trim()
                    .toLowerCase();
                if (!conversationId)
                    return;
                if (actor && actor === currentMessagingUserId()) {
                    if (event.type === "chat.read") {
                        this.clearConversationUnread(conversationId);
                    }
                    return;
                }
                this.applyReceipts(conversationId, {
                    lastReadSortKey: event.type === "chat.read"
                        ? event.lastReadSortKey ?? null
                        : undefined,
                    lastDeliveredSortKey: event.lastDeliveredSortKey ??
                        (event.type === "chat.read"
                            ? event.lastReadSortKey
                            : null) ??
                        null,
                });
                return;
            }
            if (event.type === "chat.mute.ack") {
                const conversationId = String(event.conversationId || "");
                const idx = this.conversations.findIndex((item) => item.conversationId === conversationId || item.id === conversationId);
                if (idx < 0)
                    return;
                this.conversations[idx] = {
                    ...this.conversations[idx],
                    muted: Boolean(event.isMuted ?? event.muted),
                    mutedUntil: event.mutedUntil ?? null,
                };
            }
        },
        setActiveConversation(conversationId) {
            this.activeConversationId = conversationId;
            messagingSocketService.send("chat.active", {
                conversationId,
                appState: "FOREGROUND",
            });
        },
        setAppState(isForeground) {
            this.appForeground = isForeground;
            if (isForeground) {
                void this.recoverMessaging();
                messagingSocketService.send("chat.active", {
                    conversationId: this.activeConversationId,
                    appState: "FOREGROUND",
                });
            }
            else {
                messagingSocketService.send("chat.active", {
                    conversationId: this.activeConversationId,
                    appState: "BACKGROUND",
                });
            }
        },
        setPushNavIntent(intent) {
            this.pushNavIntent = intent;
        },
    },
});
